import { Dayjs } from "dayjs";
import {
  AccessGroupsData,
  ConfigurationAccessGroupsData,
} from "../api/models/access-groups";

const WEEK_DAY_NAMES = [
  "sunday",
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
] as const;

const parseTimeToMinutes = (time: string | null | undefined): number | null => {
  if (!time) return null;
  const [hours, minutes, seconds = 0] = time.split(":").map(Number);
  if (Number.isNaN(hours) || Number.isNaN(minutes)) return null;
  return hours * 60 + minutes + seconds / 60;
};

const isTimeWithinRange = (
  currentMinutes: number,
  startTime: string,
  endTime: string,
): boolean => {
  const startMinutes = parseTimeToMinutes(startTime);
  const endMinutes = parseTimeToMinutes(endTime);
  if (startMinutes === null || endMinutes === null) return false;

  if (startMinutes <= endMinutes) {
    return currentMinutes >= startMinutes && currentMinutes <= endMinutes;
  }

  return currentMinutes >= startMinutes || currentMinutes <= endMinutes;
};

const getDayNameFromDate = (date: Dayjs) => WEEK_DAY_NAMES[date.day()];

const getInvitationTimeWindow = (params: {
  allowAllDay: boolean;
  hour: string;
  minutes: string;
  hoursLater: string;
}) => {
  if (params.allowAllDay) {
    return { startMinutes: 0, endMinutes: 23 * 60 + 59 };
  }

  const startMinutes = Number(params.hour) * 60 + Number(params.minutes);
  const endMinutes = startMinutes + Number(params.hoursLater) * 60;

  return { startMinutes, endMinutes };
};

const isInvitationWindowWithinConfig = (
  startMinutes: number,
  endMinutes: number,
  config: ConfigurationAccessGroupsData,
) => {
  const startTime = config.start_time;
  const endTime = config.end_time;
  if (!startTime || !endTime) return false;

  const scheduleStart = parseTimeToMinutes(startTime);
  const scheduleEnd = parseTimeToMinutes(endTime);
  if (scheduleStart === null || scheduleEnd === null) return false;

  const spansMidnight = endMinutes > 24 * 60;
  const normalizedEnd = spansMidnight ? endMinutes - 24 * 60 : endMinutes;

  // Same-day invitation window cannot fit into overnight-only schedules fully
  // unless both endpoints fall inside the overnight range.
  if (!spansMidnight) {
    if (scheduleStart <= scheduleEnd) {
      return startMinutes >= scheduleStart && normalizedEnd <= scheduleEnd;
    }
    return (
      isTimeWithinRange(startMinutes, startTime, endTime) &&
      isTimeWithinRange(normalizedEnd, startTime, endTime)
    );
  }

  // Invitation crosses midnight: both endpoints must be allowed by schedule.
  return (
    isTimeWithinRange(startMinutes, startTime, endTime) &&
    isTimeWithinRange(normalizedEnd, startTime, endTime)
  );
};

const configCoversDays = (
  config: ConfigurationAccessGroupsData,
  days: string[],
) => {
  const configDays = config.week_days ?? [];
  return days.some((day) => configDays.includes(day));
};

export type InvitationAccessCompatibilityInput = {
  multipleAccess: boolean;
  allowAllDay: boolean;
  date: Dayjs | null;
  hour: string;
  minutes: string;
  hoursLater: string;
  weekDaysRestriction: string[];
  accessGroups: AccessGroupsData[];
};

export type InvitationAccessCompatibilityResult = {
  compatible: boolean;
  hasAccessGroups: boolean;
  matchedAccessGroupIds: number[];
};

export const checkInvitationAccessGroupCompatibility = (
  input: InvitationAccessCompatibilityInput,
): InvitationAccessCompatibilityResult => {
  const groups = input.accessGroups ?? [];
  if (groups.length === 0) {
    return {
      compatible: false,
      hasAccessGroups: false,
      matchedAccessGroupIds: [],
    };
  }

  if (!input.date || !input.date.isValid()) {
    return {
      compatible: false,
      hasAccessGroups: true,
      matchedAccessGroupIds: [],
    };
  }

  const { startMinutes, endMinutes } = getInvitationTimeWindow({
    allowAllDay: input.allowAllDay,
    hour: input.hour,
    minutes: input.minutes,
    hoursLater: input.hoursLater,
  });

  const invitationDays = input.multipleAccess
    ? input.weekDaysRestriction
    : [getDayNameFromDate(input.date)];

  if (invitationDays.length === 0) {
    return {
      compatible: false,
      hasAccessGroups: true,
      matchedAccessGroupIds: [],
    };
  }

  const matchedAccessGroupIds: number[] = [];

  for (const group of groups) {
    const configs = group.configuration ?? [];

    const matchesGroup = input.multipleAccess
      ? invitationDays.every((day) =>
          configs.some(
            (config) =>
              (config.week_days ?? []).includes(day) &&
              isInvitationWindowWithinConfig(
                startMinutes,
                endMinutes,
                config,
              ),
          ),
        )
      : configs.some(
          (config) =>
            configCoversDays(config, invitationDays) &&
            isInvitationWindowWithinConfig(
              startMinutes,
              endMinutes,
              config,
            ),
        );

    if (matchesGroup) {
      matchedAccessGroupIds.push(group.id);
    }
  }

  return {
    compatible: matchedAccessGroupIds.length > 0,
    hasAccessGroups: true,
    matchedAccessGroupIds,
  };
};

export const getInvitationDayName = (date: Dayjs | null) => {
  if (!date || !date.isValid()) return null;
  return getDayNameFromDate(date);
};
